import { useState, useEffect } from 'react';
import SidebarProfile from '../../../../shared/components/SidebarProfile';
import { flushSync } from 'react-dom';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../../../components/common/Logo/Logo';
import { C, t } from '../../../../shared/constants/dashboardConstants';
import { IcoLogout } from '../../../../shared/icons/DashboardIcons';
import { useAuth } from '../../../../contexts/AuthContext';
import { getMyProducerProfile } from '../../../../api/producerApi';
import { getMyTalentProfile } from '../../../../api/talentApi';
import { resolveProfileImageUrl } from '../../../../shared/utils/profilePlaceholders';
import { getDefaultProfileImage } from '../../../../shared/utils/profilePlaceholders';
import { isProducerLogoRemoved } from '../../Company/ProducerCompanyPage';

// 대행(MANAGED) 계정에 보일 메뉴 — 위임 범위가 '공고·지원자 관리'이고,
// 비밀번호를 다시 바꿀 수 있어야 하므로 계정 설정은 남긴다.
// 대행 계정에 보여줄 메뉴. 나머지는 숨기고 URL도 PrivateRoute에서 막는다.
// 계정 설정은 아래 전환 카드로 들어간다.
const MANAGED_MENU_IDS = ['projects'];

const MENU = [
  { id: 'dashboard', label: '대시보드', path: '/producer/dashboard', iconSrc: '/assets/images/dashboard.svg' },
  { id: 'company', label: '제작사 정보', path: '/producer/company', iconSrc: '/assets/images/studio.svg' },
  { id: 'projects', label: '공고·지원자 관리', path: '/producer/projects', iconSrc: '/assets/images/application.svg' },
  { id: 'archive', label: '보관함', path: '/producer/archive', iconSrc: '/assets/images/board.svg' },
  { id: 'messages', label: '메시지', path: '/producer/messages', iconSrc: '/assets/images/message.svg', comingSoon: true },
  { id: 'schedule', label: '일정 관리', path: '/producer/schedule', iconSrc: '/assets/images/calendar.svg' },
  { id: 'settings', label: '계정 설정', path: '/producer/settings', iconSrc: '/assets/images/settings.svg' },
];

function MenuIcon({ src, color }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: '24px',
        height: '24px',
        flexShrink: 0,
        display: 'block',
        backgroundColor: color,
        WebkitMaskImage: `url(${src})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskImage: `url(${src})`,
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: 'contain',
      }}
    />
  );
}


function LogoutConfirmModal({ onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '20px', padding: '36px 40px', width: '360px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#090909', marginBottom: '8px' }}>로그아웃</div>
          <div style={{ fontSize: '15px', fontWeight: 400, color: '#888896' }}>로그아웃 하시겠습니까?</div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onCancel} style={{ flex: 1, height: '48px', borderRadius: '12px', border: '1px solid #E9E9EC', background: '#fff', cursor: 'pointer', fontSize: '15px', fontWeight: 600, color: '#090909' }}>취소</button>
          <button onClick={onConfirm} style={{ flex: 1, height: '48px', borderRadius: '12px', border: 'none', background: '#090909', cursor: 'pointer', fontSize: '15px', fontWeight: 600, color: '#fff' }}>로그아웃</button>
        </div>
      </div>
    </div>
  );
}

function ProducerSidebar({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, role, isTalent, isManagedAccount, logout, userName, userProfileImage, setUserInfo, setProfile, switchRole } = useAuth();

  const menu = isManagedAccount ? MENU.filter(m => MANAGED_MENU_IDS.includes(m.id)) : MENU;
  const activeId = menu.find(m => location.pathname.startsWith(m.path))?.id
    || (location.pathname.startsWith('/producer/castings') ? 'projects' : 'dashboard');

  // role이 PRODUCER로 세팅될 때마다 제작사 프로필 갱신
  useEffect(() => {
    if (!isLoggedIn || role !== 'PRODUCER') return;
    getMyProducerProfile()
      .then(p => {
        setProfile(p.id);
        const logoRemoved = isProducerLogoRemoved();
        const img = logoRemoved ? getDefaultProfileImage('PRODUCER') : (p.logoUrl || p.profileImage || p.imageUrl || getDefaultProfileImage('PRODUCER'));
        setUserInfo({ name: p.companyName, profileImage: img, stageName: null, realName: null });
      })
      .catch(() => {});
  }, [isLoggedIn, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSwitchToTalent = () => {
    flushSync(() => { switchRole('TALENT'); });
    navigate('/talent/dashboard');
  };
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const producerDefaultProfileImage = getDefaultProfileImage('PRODUCER');
  const resolvedProducerProfileImage = userProfileImage || producerDefaultProfileImage;
  const isProducerDefaultProfileImage = resolvedProducerProfileImage === producerDefaultProfileImage;

  const sidebarStyle = {
    position: 'fixed', left: 0, top: 0, height: '100vh',
    background: C.bg, borderRight: `1px solid ${C.g200}`,
    display: 'flex', flexDirection: 'column', zIndex: 200,
    transition: 'transform 0.25s ease',
    width: 'clamp(240px, 25vw, 342px)',
  };

  return (
    <>
      {open && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 199 }} />}
      <aside className={`sidebar-drawer${open ? ' sidebar-open' : ''}`} style={sidebarStyle}>
      <div className="sidebar-logo" onClick={() => navigate('/')}>
        <Logo />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <SidebarProfile
          src={resolvedProducerProfileImage}
          name={userName}
          role="제작사"
          isDefaultImage={isProducerDefaultProfileImage}
        />
        {/* <div style={{ position: 'relative', cursor: 'pointer' }}>
          <IcoBell />
          <div style={{ position: 'absolute', top: '-2px', right: '-2px', width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)' }} />
        </div> */}
      </div>

      <div style={{ borderTop: `1px solid ${C.g200}`, margin: '12px 0' }} />

      <div style={{ padding: '10px 24px' }}>
        <div style={{ background: C.g200, borderRadius: '10px', padding: '5px', display: 'flex', position: 'relative' }}>
          {isTalent && <div style={{ position: 'absolute', right: 5, top: 5, width: 'calc(50% - 5px)', height: '46px', background: C.white, borderRadius: '6px', boxShadow: C.shadow }} />}
          {isTalent ? (
            <button
              onClick={handleSwitchToTalent}
              style={{ flex: 1, height: '46px', background: 'transparent', border: 'none', cursor: 'pointer', ...t('18px', 600, C.sub), position: 'relative', zIndex: 1 }}
            >배우</button>
          ) : (
            <button
              onClick={() => navigate('/producer/settings')}
              style={{ flex: 1, height: '46px', background: 'transparent', border: 'none', cursor: 'pointer', ...t('18px', 600, C.g300), position: 'relative', zIndex: 1 }}
            >배우</button>
          )}
          <button style={{ flex: 1, height: '46px', background: 'transparent', border: 'none', cursor: 'default', ...t('18px', 600, C.bk), position: 'relative', zIndex: 1 }}>제작사</button>
        </div>
      </div>

      <nav style={{ padding: '10px 24px', flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {menu.map(({ id, label, path, iconSrc, comingSoon }) => {
          const active = id === activeId;
          return (
            <button key={id} onClick={() => navigate(path)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '22px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: active ? C.bk : 'transparent', width: '100%', textAlign: 'left', flexShrink: 0 }}>
              <MenuIcon src={iconSrc} color={active ? C.white : C.g400} />
              <span style={t('18px', 600, active ? C.white : C.g400)}>{label}</span>
              {comingSoon && (
                <span style={{
                  marginLeft: 'auto', padding: '2px 8px', borderRadius: '100px',
                  background: active ? 'rgba(255,255,255,0.18)' : C.g200,
                  ...t('11px', 600, active ? C.white : C.g400),
                }}>
                  준비 중
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* 대행 계정 — 정식 전환 유도. 상시 노출하되 작업을 가리지 않는 크기로. */}
      {isManagedAccount && (
        <div style={{ padding: '0 20px 16px', flexShrink: 0 }}>
          <div style={{
            border: `1px solid ${C.g200}`, borderRadius: '14px', padding: '16px',
            background: C.bg, display: 'flex', flexDirection: 'column', gap: '8px',
          }}>
            <span style={t('15px', 700, C.bk)}>더 많은 기능이 있어요</span>
            <span style={{ ...t('13px', 400, C.sub), lineHeight: 1.5 }}>
              공고 등록 · 보관함 · 일정 관리까지<br />지금 계정 그대로 사용할 수 있습니다.
            </span>
            <button
              onClick={() => navigate('/producer/settings?upgrade=1')}
              style={{
                marginTop: '4px', height: '40px', borderRadius: '10px', border: 'none',
                background: C.bk, cursor: 'pointer', ...t('14px', 600, C.white),
              }}
            >
              정식 계정으로 전환
            </button>
          </div>
        </div>
      )}

      <div style={{ borderTop: `1px solid ${C.g200}`, padding: '20px 24px', flexShrink: 0 }}>
        <button onClick={() => setShowLogoutConfirm(true)} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <IcoLogout />
          <span style={t('18px', 600, C.g400)}>로그아웃</span>
        </button>
      </div>
      {showLogoutConfirm && createPortal(
        <LogoutConfirmModal
          onConfirm={async () => { await logout(); window.location.replace('/'); }}
          onCancel={() => setShowLogoutConfirm(false)}
        />,
        document.body
      )}
    </aside>
    </>
  );
}

export default ProducerSidebar;
